/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef } from '@angular/core';
import { NavRoute } from '@src/app/shared/NavRoute';
import { AbstractObject, TenantId } from '@src/app/model/abstract-object';
import { Params, Router, RouterLink } from '@angular/router';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { MyInjector } from '@src/app/app.module';
import { ButtonControl } from '@src/app/shared/form/form-button/form-button.component';
import { ControlOn } from '@src/app/shared/form/app-form-control';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Txn } from '@src/app/model/txn';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';
import { CreditNotePageNavRoute } from '@src/app/routes/credit-note-page.nav';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';
import { BankInPageNavRoute } from '@src/app/routes/bank-in-page-nav';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';
import { BankPageNavRoute } from '@src/app/routes/bank-page-nav';
import { JournalPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-form-nav-link',
    imports: [MatIconModule, MatTooltipModule, RouterLink],
    templateUrl: './form-nav-link.component.html',
    styleUrl: './form-nav-link.component.scss'
})
export class FormNavLinkComponent implements FormControlInterface {
    @Input() control: ButtonControl;
    @Input() on: ControlOn = 'form';

    public static make(label: string, nav: NavRoute,
        navParmCalculator: (o: AbstractObject) => Params, options: Partial<ButtonField> = {}): ButtonField {
        const field = new ButtonField({ value: label, label, sendServer: false, type: 'link' }, options);
        /* These parms drive navigation from table */
        field.btnOpts.navParmCalculator = navParmCalculator;
        if (!options.btnOpts?.navRouteCalculator) {
            field.btnOpts.navRouteCalculator = () => nav.getListRouteName();
        }

        /** These parms will set it up when used on form */
        field.btnOpts.navItem = nav;

        // Actual params will be calculated when field value is set...
        field.btnOpts.clickMethod = () => {
            const router = MyInjector.instance.get(Router);
            router.navigate([nav.getListRouteName(), field.btnOpts.navParms])
        };
        field.formControlFactory = FormNavLinkComponent.createComponent;
        return field;
    }

    public static makeForced(label: string, nav: NavRoute,
        forceToTenant: (o: AbstractObject) => TenantId,
        navParmCalculator: (o: AbstractObject) => Params = null, 
        options: Partial<ButtonField> = {}): ButtonField {
        const field = new ButtonField({ value: label, label, sendServer: false, type: 'link' }, options);
        /* These parms drive navigation from table */
        field.btnOpts.forceToTenant = forceToTenant;
        navParmCalculator = navParmCalculator ? navParmCalculator : () => ({});
        field.btnOpts.navParmCalculator = navParmCalculator;
        if (!options.btnOpts?.navRouteCalculator) {
            field.btnOpts.navRouteCalculator = (o: AbstractObject) => {
                const toTenantId = field.btnOpts.forceToTenant(o)
                if (toTenantId) {
                    return nav.getForceListRouteName(toTenantId);
                } else {
                    return null;
                }
            }
        }

        if (!field.hint) {
            field.hint = $localize `Click to navigate to related ${field.label}`;
        }

        /** These parms will set it up when used on form */
        field.btnOpts.navItem = nav;

        field.formControlFactory = FormNavLinkComponent.createComponent;
        return field;
    }

    public static makeDetail(label: string, displayValue: string|((o:AbstractObject) => string|number), idValue: string, nav: NavRoute, forceTeam = false): ButtonField {
        let field;
        if (typeof displayValue === 'string') {
            field = new ButtonField({ value: displayValue, label, sendServer: false, type: 'link' });
        } else {
            field = new ButtonField({ value: label, label, sendServer: false, type: 'link', calculateValue: displayValue });
        }

        field.btnOpts.navItem = nav;

        field.btnOpts.navRouteCalculator = (o) => {
            const v = field.resolveValue(idValue, o);
            if (v) {
                return nav.getIdUrl(v, forceTeam ? o.teamId : undefined);
            }
            return null; // Do not navigate if no id value on item provided
        };
        field.btnOpts.navParmCalculator = () => { return {} };
        field.formControlFactory = FormNavLinkComponent.createComponent;
        return field;
    }

    public static makeTxnLink(label: string, related = false) {
        // Purchases depend on the name field not changing!
        let fName = 'refNrLink';
        const calculateValue = (o: Txn) => {
            if (related && o.related) {
                fName = 'related_refNrLink';
                return o.related?.refNr
            } else {
                if (o.parentRefNr) {
                    return o.parentRefNr;
                }
                return o.refNr
            }
        }
        const field = new ButtonField({ value: fName, label, name: fName, sendServer: false, type: 'link', calculateValue });
        field.btnOpts.navParmCalculator = () => { return {} };
        field.cellOpts = { width: "6em", maxWidth: "10em", heading: label };

        field.btnOpts.navRouteCalculator = (o: Txn) => {
            if (o) {
                o = (related && o.related) ? o.related : o;
                const txnRoute = FormNavLinkComponent.getTxnRoute(o);
                if (txnRoute?.route) {
                    return txnRoute.route.getIdUrl(txnRoute.useParent ? o.parentId : o.id);
                } else {
                    console.log('Do not know how to navigate to ', {o: txnRoute})
                }
            }
            return null; // Do not navigate if no id value on item provided
        };

        field.formControlFactory = FormNavLinkComponent.createComponent;
        return field;        
    }

    public static createComponent(vcr: ViewContainerRef, ctl: ButtonControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormNavLinkComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }
    onClick($event) {
        // Disabled buttons do not show tooltip, we need tooltip for budget icons... but do not allow click readonly button
        if (this.control.field.btnOpts.clickMethod && !this.control.field.disable) {
            this.control.field.btnOpts.clickMethod(this.control, $event);
        }
    }

    static getTxnRoute(o: Txn) : {route: NavRoute, useParent?: boolean}{
        if (o) {
            if (o.typeId === Txn.TYPE.INVOICE.id) {
                return {route: InvoicePageNavRoute};
            } else if (o.typeId === Txn.TYPE.INVOICE_ITEM.id || o.typeId === Txn.TYPE.CHARGES_VAT.id) {
                return {route: InvoicePageNavRoute, useParent: true};
            } else if (o.typeId === Txn.TYPE.BANK_IN.id) {
                return {route: BankInPageNavRoute};
            } else if (o.typeId === Txn.TYPE.BANK_IN_ALLOC.id || o.typeId === Txn.TYPE.BANK_IN_ON_ACCT.id || o.typeId === Txn.TYPE.BANK_IN_DISCOUNT.id) {
                return {route: BankInPageNavRoute, useParent: true};
            } else if (o.typeId === Txn.TYPE.JRNL.id) {
                return {route: JournalPageNavRoute};
            } else if ([
                Txn.TYPE.JRNL_DETAIL.id,
                Txn.TYPE.YEAREND_DETAIL.id,
                Txn.TYPE.SURPLUS.id
            ].indexOf(+o.typeId) >= 0) {
                return {route: JournalPageNavRoute, useParent: true};
            } else if ([
                Txn.TYPE.BANK_CHARGE.id,
                Txn.TYPE.BANK_TRAN.id,
                Txn.TYPE.BANK_INT_RECD.id
            ].indexOf(+o.typeId) >= 0) {
                return {route: BankPageNavRoute};
            } else if ([
                Txn.TYPE.BANK_CHG_DTL.id,
                Txn.TYPE.BANK_TRAN_IN.id,
                Txn.TYPE.BANK_INT_INCOME.id
            ].indexOf(+o.typeId) >= 0) {
                return {route: BankPageNavRoute, useParent: true};
            } else if (o.typeId === Txn.TYPE.CREDIT_NOTE.id) {
                return {route: CreditNotePageNavRoute};
            } else if (o.typeId === Txn.TYPE.CREDIT_NOTE_ITEM.id) {
                return {route: CreditNotePageNavRoute, useParent: true};
            } else if ([
                Txn.TYPE.PURCHASE.id, 
                Txn.TYPE.CASH_PURCHASE.id,
                Txn.TYPE.PURCHASE_CN.id,
                Txn.TYPE.PURCHASE_ACCRUAL.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL.id
            ].indexOf(o.typeId as number) >= 0) {
                return {route: PurchasePageNavRoute};
            } else if ([
                Txn.TYPE.PURCHASE_ITEM.id,
                Txn.TYPE.CASH_PURCHASE_ITEM.id,
                Txn.TYPE.PURCHASE_CN_ITEM.id,
                Txn.TYPE.PURCHASE_CN_APPLY.id,
                Txn.TYPE.PURCHASE_CN_USED.id,
                Txn.TYPE.PURCHASE_VAT.id,
                Txn.TYPE.PREPAY_WRITEDOWN.id,
                Txn.TYPE.PREPAY_CHARGE.id,
                Txn.TYPE.PREPAY_ASSET.id,
                Txn.TYPE.PREPAY_REV_EXP.id,
                Txn.TYPE.PURCHASE_ACCRUAL_ITEM.id,
                Txn.TYPE.PURCHASE_ACCRUAL_REV.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL_ITEM.id,
                Txn.TYPE.PURCHASE_CN_ACCRUAL_REV.id,
            ].indexOf(+o.typeId) >= 0) {
                return {route: PurchasePageNavRoute, useParent: true};
            } else if (o.typeId === Txn.TYPE.BANK_OUT_ALLOC.id) {
                return {route: BankOutPageNavRoute, useParent: true};
            } else if (o.typeId === Txn.TYPE.BANK_OUT.id) {
                return {route: BankOutPageNavRoute};
            } else {
                console.warn('dunnohowtolinkto ' + o.typeId, o);
                return {route: undefined}
            }
        } else {
            // Called with null for headers of grids...
            return {route: undefined};
        }
    }    
}
